import axios, { AxiosResponse } from "axios";
import { API_URL } from "../constants";
import { UserInfo } from "../contexts/UserInfoContext";
import { resolveAccessToken } from "./Auth";
import { canAccessPersona } from "@common/domain/auth";
import { config } from "@config";
import { isPersonaInCompanyCategory } from "./personaCategory";

export interface Persona {
	id?: string,
    tenantId?: string,
    category?: string,
	replicaId?: string,
    devPersonaId?: string,
    localPersonaId?: string,
    dev_persona_id?: string,
    local_persona_id?: string,
	name?: string,
	title?: string,
	temperature?: number,
    responseMaxTokens?: number,
    enableWebSearch?: boolean,
	imageURL?: string,
	personality?: string,
	avatar_url?: string,
    identity?: string,
    audioStyle?: string,
    theologicalFrameworks?: string,
    modeGuidance?: {
        smallTalk?: string,
        straightTalk?: string,
        aligned?: string,
        caution?: string,
        neutralSegue?: string,
        blocked?: string,
    },
    promptConfig?: any[],
	voiceId?: string,
    discoveryGuideProfileId?: string,
}

export async function getPersonas(userInfo: UserInfo): Promise<Persona[]> {
    const token = await resolveAccessToken(userInfo?.accessToken);
    if (!token) {
        throw new Error("SESSION_EXPIRED");
    }

    try {
        const response: AxiosResponse = await axios.get(
            `${API_URL}/personas`,
            { headers: { Authorization: `Bearer ${token}` } }
        );

        let data = Array.isArray(response.data) ? response.data : [];
        data = data.filter((p: Persona) => isPersonaInCompanyCategory(p, config.companyName));
        return data
            .map((p: Persona) => ({
                ...p,
                promptConfig: Array.isArray(p.promptConfig) ? p.promptConfig : []
            }))
            .filter((p: Persona) => canAccessPersona(p, userInfo));
    } catch (error) {
        throw error;
    }
}

export async function getPersona(userInfo: UserInfo, personaId: string): Promise<Persona | undefined> {
    const token = await resolveAccessToken(userInfo?.accessToken);
    if (!token) {
        throw new Error("SESSION_EXPIRED");
    }

    try {
        const response: AxiosResponse = await axios.get(
            `${API_URL}/persona/${personaId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );

        const persona = response.data;
        if (persona) {
            if (!isPersonaInCompanyCategory(persona, config.companyName)) {
                return undefined;
            }
            persona.promptConfig = Array.isArray(persona.promptConfig) ? persona.promptConfig : [];
            if (!canAccessPersona(persona, userInfo)) {
                return undefined;
            }
        }

        return persona;
    } catch (error) {
        throw error;
    }
}

export async function updatePersona(
	userInfo: UserInfo | undefined,
	personaId: string,
	updates: Partial<Persona>
) {
    const token = await resolveAccessToken(userInfo?.accessToken);
    if (token) {
        try {
            const response: AxiosResponse = await axios.put(
                `${API_URL}/persona/${personaId}`,
                updates,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            throw error
        }
    } else return undefined;
}

export async function deletePersona(
    userInfo: UserInfo | undefined,
    personaId: string
) {
    const token = await resolveAccessToken(userInfo?.accessToken);
    if (token) {
        try {
            const response: AxiosResponse = await axios.delete(
                `${API_URL}/persona/${personaId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            throw error;
        }
    } else return undefined;
}

export async function createPersona(
	userInfo: UserInfo | undefined,
	persona: Partial<Persona>
) {
	const token = await resolveAccessToken(userInfo?.accessToken);
	if (token) {
        try {
            const response: AxiosResponse = await axios.post(
                `${API_URL}/persona/template`,
                persona,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            throw error;
        }
    } else return undefined;
}

export interface PersonaCacheRefreshResult {
    success: boolean;
    status?: number;
    message?: string;
}

export async function refreshPersonaCache(
    userInfo?: UserInfo
): Promise<PersonaCacheRefreshResult> {
    const token = await resolveAccessToken(userInfo?.accessToken);
    if (!token) {
        throw new Error("SESSION_EXPIRED");
    }

    try {
        const response: AxiosResponse = await axios.post(
            `${API_URL}/admin/personas/cache/refresh`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return { success: true, status: response.status };
    } catch (error: any) {
        if (error.response?.status === 429) {
            return {
                success: false,
                status: 429,
                message: error.response?.data?.error || "Persona cache was refreshed recently; try again in 10 seconds.",
            };
        }
        if (error.response?.status === 403) {
            return {
                success: false,
                status: 403,
                message: error.response?.data?.error || "Forbidden: Cognito admin access required",
            };
        }
        throw error;
    }
}

